import type { MouseEvent } from 'react';
import { tabColor } from '../tabPalette';
import { runningAsApp } from '../utils/installApp';
import { inSplitSide, samePagePath } from './splitView';

// ── Right-click a bubble or a tile to open its page in a new browser tab (Calem, report ab347131) ────────────────
// "Allow users to launch any tile/menu page as a new tab by right-clicking that menu bubble also allow this in the
// user page."
// Every bubble and tile that until now only swapped the view in place takes this on top of its ordinary click: the
// Dashboard's top-menu tabs (components/Portal), the ≡ menu's pages, the buttons and dock tiles of a My Dashboard(s)
// menu (components/FocusDashboard) and the User page's Statistics blocks (components/UserPage). It opens the page the
// way the ≡ menu's entries and the rocket's list of dashboards have always opened theirs — window.open(url, '_blank')
// — and holds the browser's own context menu back while it does. A left click is untouched everywhere, and so is
// every bubble with no page behind it (Sandbox, Chat, Change, Exit).

/** A page's own address, the one the ≡ menu's Default page select sends a sign-in to as well (services/homePage.ts):
 *  a page with a top-menu button is a tab of the Dashboard's strip and lives at /?tab=<key> — the only address
 *  Quotation, Invoice, Receipt, Approvals and the Client account have, /m/<key> being the "under development" stub
 *  for them — while a page with none (every ≡-menu module) is a page of its own at /m/<key>. tabColor tells the two
 *  apart (tabPalette: boxOf), the same test the menus already take to colour the bubble. */
export const pageHref = (key: string) => tabColor(key)
  ? `/?tab=${encodeURIComponent(key)}`
  : `/m/${encodeURIComponent(key)}`;

/** What the right-click asks the menu (components/SplitView: OpenMenuHost) to offer, and where to put it up. */
export const OPEN_MENU_EVENT = 'slf:open-menu';
export type OpenMenuAsk = { url: string; x: number; y: number };

/** The right-click itself, for a bubble's or a tile's onContextMenu: the browser's own menu stands aside and the page
 *  opens in a tab of its own. An external address (the Social Poster, a separate app) is opened as it stands.
 *  In the installed app (report 60878cab), which has no browser tabs and so no Chrome split view, the right-click puts
 *  up a small menu instead: "Open in new window" (the same window.open as before) or "Open in split view". A side of a
 *  split view asks the same, whatever the window. A browser tab keeps the plain new tab. */
export const openInNewTab = (url: string) => (e: MouseEvent) => {
  e.preventDefault();
  if ((runningAsApp() || inSplitSide()) && samePagePath(url)) {
    const ask: OpenMenuAsk = { url, x: e.clientX, y: e.clientY };
    window.dispatchEvent(new CustomEvent<OpenMenuAsk>(OPEN_MENU_EVENT, { detail: ask }));
    return;
  }
  window.open(url, '_blank');
};
